import { useState, useEffect, useMemo } from 'react';
import { CalendarIcon, Settings, Calendar, Info, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { AvailabilityRule, AvailabilityAction, OTAChannel, SelectedVariant } from '@/types/revenue-manager';
import { useRoomTypes } from '@/hooks/use-room-inventory';
import { transformAvailabilityRuleForAPI, ApiAvailabilityRule } from '@/utils/availability-rule-transform';

interface CreateAvailabilityRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (rule: ApiAvailabilityRule) => void; // Now accepts API format
  otaChannels: OTAChannel[];
  editingRule?: AvailabilityRule | null;
  branchId?: string | number;
  orgId?: string | number;
  propertyId?: string | number;
  api_key?: string | number;
  isLoading?: boolean;
}

const actionTypes = [
  { value: 'setStatus', label: 'Set Status (Open/Closed/CTA/CTD)' },
  { value: 'setMinStay', label: 'Set Minimum LOS' },
  { value: 'setMaxStay', label: 'Set Maximum LOS' },
  // { value: 'setExactStay', label: 'Set Exact LOS' },
  { value: 'setAvailability', label: 'Set Availability' },
  { value: 'adjustAvailability', label: 'Adjust Availability' },
];

const statusValues = [
  { value: 'Open', label: 'Open' },
  { value: 'Closed', label: 'Closed' },
  { value: 'CTA', label: 'Closed to Arrival' },
  { value: 'CTD', label: 'Closed to Departure' },
];

const frequencies = [
  { value: 'manual', label: 'Manual Only' },
  { value: 'immediate', label: 'Run Once Immediately' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly (Multiple Days)' },
  { value: 'monthly', label: 'Monthly (Multiple Days)' },
  { value: 'specific', label: 'Specific Date & Time' },
];

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function CreateAvailabilityRuleDialog({
  open,
  onOpenChange,
  onSave,
  otaChannels,
  editingRule,
  branchId,
  orgId,
  propertyId,
  api_key,
  isLoading = false,
}: CreateAvailabilityRuleDialogProps) {
  const { data: roomTypesData, isLoading: isLoadingRooms } = useRoomTypes(branchId);
  
  // Transform room types from booking scheduler API
  const roomTypesWithVariants = useMemo(() => {
    if (!roomTypesData) return [];
    return roomTypesData.map(item => ({
      id: item.id.toString(),
      name: `${item.room_type} (${item.HotelBranchData.branch_name})`,
      adults: item.num_of_adult_max,
      children: item.num_of_child_max,
      basePrice: item.basic_room_rate,
      roomCount: item?.HotelRoomData?.length || item.room_count,
      variants: (item.HotelRoomTypeVarientRelData || []).map(v => ({
        id: v.id.toString(),
        name: v.VarientRoomTypeDataRel?.name || 'Unknown Variant',
        code: v.variant_wise_code,
        basePrice: v.computed_base_price || v.VarientRoomTypeDataRel?.base_price || '0',
        maxAdults: v.VarientRoomTypeDataRel?.max_adults || 0,
        maxChildren: v.VarientRoomTypeDataRel?.max_children || 0,
      }))
    }));
  }, [roomTypesData]);
  const [name, setName] = useState('');
  const [priority, setPriority] = useState(1);
  const [description, setDescription] = useState('');
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariant[]>([]);
  const [selectedOTAChannels, setSelectedOTAChannels] = useState<string[]>([]);
  const [applicateCmNames, setApplicateCmNames] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<AvailabilityAction[]>([{ adjustmentType: 'setStatus', adjustmentValue: 'Open' }]);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [timeOfDay, setTimeOfDay] = useState('all');
  const [processingFrequency, setProcessingFrequency] = useState<AvailabilityRule['processingFrequency']>('manual');
  const [executionDays, setExecutionDays] = useState<number[]>([]);
  const [executionTime, setExecutionTime] = useState('00:00');
  const [executionStartDate, setExecutionStartDate] = useState<Date | undefined>(undefined);
  const [executionEndDate, setExecutionEndDate] = useState<Date | undefined>(undefined);

  // Debug: Log otaChannels prop
  // useEffect(() => {
  //   console.log('OTA Channels available in Availability dialog:', otaChannels);
  // }, [otaChannels]);

  useEffect(() => {
    if (editingRule) {
      console.log('Editing rule:', editingRule);
      setName(editingRule.name);
      setPriority(editingRule.priority);
      setDescription(editingRule.description || '');
      setSelectedRoomTypes(editingRule.roomTypes);
      
      // Enrich selectedVariants with roomTypeId if missing
      const enrichedVariants = (editingRule.selectedVariants || []).map((variant) => {
        // If variant already has roomTypeId, return as is
        if (variant.roomTypeId && variant.roomTypeId !== '') {
          return variant;
        }
        
        // Otherwise, find the roomTypeId from roomTypesData
        if (roomTypesData) {
          for (const roomType of roomTypesData) {
            const foundVariant = roomType.HotelRoomTypeVarientRelData?.find(
              (v: any) => String(v.id) === String(variant.variantId)
            );
            if (foundVariant) {
              return {
                ...variant,
                roomTypeId: String(roomType.id),
                variantCode: variant.variantCode || foundVariant.variant_wise_code || `V${variant.variantId}`,
                variantName: variant.variantName || foundVariant.VarientRoomTypeDataRel?.name || 'Unknown Variant'
              };
            }
          }
        }
        return variant;
      });
      
      setSelectedVariants(enrichedVariants);
      setSelectedOTAChannels(editingRule.otaChannels || []);
      setApplicateCmNames((editingRule as any).applicateCmNames || []);
      
      // Support both new action array and legacy actionType/actionValue
      console.log('Loading editingRule actions:', {
        hasAction: !!editingRule.action,
        actionLength: editingRule.action?.length,
        action: editingRule.action,
        isActionArray: Array.isArray(editingRule.action),
        hasActionsLegacy: !!editingRule.actions,
        actionsLegacy: editingRule.actions,
        actionType: editingRule.actionType,
        actionValue: editingRule.actionValue
      });
      console.log('Editing rule actions:', editingRule.action);
      if (editingRule.action && Array.isArray(editingRule.action) && editingRule.action.length > 0) {
        console.log('✅ Setting actions from action array (GET API format):', editingRule.action);
        setSelectedActions(editingRule.action);
      } else if (editingRule.actions && editingRule.actions.length > 0) {
        console.log('Setting actions from legacy actions array:', editingRule.actions);
        setSelectedActions(editingRule.actions);
      } else if (editingRule.actionType && editingRule.actionValue !== undefined && editingRule.actionValue !== null) {
        console.log('Setting actions from legacy actionType/actionValue');
        // Map old action types to new ones
        const mappedType = editingRule.actionType === 'setAllotment' ? 'setAvailability' 
          : editingRule.actionType === 'adjustAllotment' ? 'adjustAvailability' 
          : editingRule.actionType;
        setSelectedActions([{ adjustmentType: mappedType as AvailabilityAction['adjustmentType'], adjustmentValue: editingRule.actionValue }]);
      } else {
        // Fallback to default if no action data is present
        console.log('No action data found, using default');
        setSelectedActions([{ adjustmentType: 'setStatus', adjustmentValue: 'Open' }]);
      }
      
      setStartDate(new Date(editingRule.startDate));
      setEndDate(editingRule.endDate ? new Date(editingRule.endDate) : undefined);
      setSelectedDays(editingRule.daysOfWeek || []);
      setTimeOfDay(editingRule.timeOfDay || 'all');
      setProcessingFrequency(editingRule.processingFrequency);
      setExecutionDays(editingRule.executionDays || []);
      setExecutionTime(editingRule.executionTime || '00:00');
      setExecutionStartDate(editingRule.executionStartDate ? new Date(editingRule.executionStartDate) : undefined);
      setExecutionEndDate(editingRule.executionEndDate ? new Date(editingRule.executionEndDate) : undefined);
    } else {
      setName('');
      setPriority(1);
      setDescription('');
      setSelectedRoomTypes([]);
      setSelectedVariants([]);
      setSelectedOTAChannels([]);
      setApplicateCmNames([]);
      setSelectedActions([{ adjustmentType: 'setStatus', adjustmentValue: 'Open' }]);
      setStartDate(new Date());
      setEndDate(undefined);
      setSelectedDays([]);
      setTimeOfDay('all');
      setProcessingFrequency('manual');
      setExecutionDays([]);
      setExecutionTime('00:00');
      setExecutionStartDate(undefined);
      setExecutionEndDate(undefined);
    }
  }, [editingRule, open, roomTypesData]);

  const toggleDay = (dayIndex: number) => {
    setSelectedDays((prev) =>
      prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const toggleExecutionDay = (day: number) => {
    setExecutionDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleRoomType = (roomTypeId: string) => {
    const isCurrentlySelected = selectedRoomTypes.includes(roomTypeId);
    
    if (isCurrentlySelected) {
      // When deselecting room type, also remove its variants
      setSelectedRoomTypes((prev) => prev.filter((id) => id !== roomTypeId));
      setSelectedVariants((variants) => variants.filter((v) => v.roomTypeId !== roomTypeId));
    } else {
      // When selecting room type, also select all its variants
      setSelectedRoomTypes((prev) => [...prev, roomTypeId]);
      const roomType = roomTypesWithVariants.find(rt => rt.id === roomTypeId);
      if (roomType && roomType.variants.length > 0) {
        const newVariants = roomType.variants.map(v => ({
          roomTypeId: roomTypeId,
          variantId: v.id,
          variantCode: v.code,
          variantName: v.name
        }));
        setSelectedVariants((prev) => [...prev, ...newVariants]);
      }
    }
  };

  const toggleVariant = (roomTypeId: string, variantId: string, variantCode: string, variantName: string) => {
    setSelectedVariants((prev) => {
      const exists = prev.some((v) => v.roomTypeId === roomTypeId && v.variantId === variantId);
      if (exists) {
        const newVariants = prev.filter((v) => !(v.roomTypeId === roomTypeId && v.variantId === variantId));
        // If no variants left for this room type, uncheck the room type as well
        const hasVariantsForRoomType = newVariants.some(v => v.roomTypeId === roomTypeId);
        if (!hasVariantsForRoomType) {
          setSelectedRoomTypes(prevRoomTypes => prevRoomTypes.filter(id => id !== roomTypeId));
        }
        return newVariants;
      }
      return [...prev, { roomTypeId, variantId, variantCode, variantName }];
    });
  };

  const isVariantSelected = (roomTypeId: string, variantId: string) => {
    return selectedVariants.some((v) => v.roomTypeId === roomTypeId && v.variantId === variantId);
  };

  const getVariantsForRoomType = (roomTypeId: string) => {
    return selectedVariants.filter((v) => v.roomTypeId === roomTypeId);
  };

  const selectAllVariantsForRoomType = (roomTypeId: string) => {
    const roomType = roomTypesWithVariants.find(rt => rt.id === roomTypeId);
    if (!roomType) return;
    
    const currentlySelected = getVariantsForRoomType(roomTypeId);
    const allSelected = currentlySelected.length === roomType.variants.length;
    
    if (allSelected) {
      // Deselect all variants for this room type
      setSelectedVariants(prev => prev.filter(v => v.roomTypeId !== roomTypeId));
    } else {
      // Select all variants for this room type
      const newVariants: SelectedVariant[] = roomType.variants.map(v => ({
        roomTypeId,
        variantId: v.id,
        variantCode: v.code,
        variantName: v.name,
      }));
      setSelectedVariants(prev => [
        ...prev.filter(v => v.roomTypeId !== roomTypeId),
        ...newVariants,
      ]);
    }
  };

  const selectAllRoomTypes = () => {
    const allRoomTypeIds = roomTypesWithVariants.map(rt => rt.id);
    // Select all room types and all their variants
    setSelectedRoomTypes(allRoomTypeIds);
    const allVariants: SelectedVariant[] = [];
    roomTypesWithVariants.forEach(roomType => {
      if (roomType.variants.length > 0) {
        roomType.variants.forEach(variant => {
          allVariants.push({
            roomTypeId: roomType.id,
            variantId: variant.id,
            variantCode: variant.code,
            variantName: variant.name
          });
        });
      }
    });
    setSelectedVariants(allVariants);
  };

  const toggleOTAChannel = (code: string) => {
    setSelectedOTAChannels((prev) => {
      const newChannels = prev.includes(code)
        ? prev.filter((id) => id !== code)
        : [...prev, code];
      console.log('Toggle OTA Channel - Previous:', prev, 'New:', newChannels);
      return newChannels;
    });
  };

  const getSelectedDayNames = () => {
    return selectedDays.map((i) => daysOfWeek[i]).join(', ');
  };

  // Action management helpers
  const toggleActionType = (adjustmentType: AvailabilityAction['adjustmentType']) => {
    const exists = selectedActions.find(a => a.adjustmentType === adjustmentType);
    if (exists) {
      setSelectedActions(prev => prev.filter(a => a.adjustmentType !== adjustmentType));
    } else {
      let defaultValue: string | number = 'Open';
      if (adjustmentType === 'setStatus') defaultValue = 'Open';
      else if (adjustmentType === 'setMinStay' || adjustmentType === 'setMaxStay' || adjustmentType === 'setExactStay') defaultValue = 2;
      else if (adjustmentType === 'setAvailability' || adjustmentType === 'adjustAvailability') defaultValue = 0;
      setSelectedActions(prev => [...prev, { adjustmentType, adjustmentValue: defaultValue }]);
    }
  };

  const updateActionValue = (adjustmentType: AvailabilityAction['adjustmentType'], adjustmentValue: string | number) => {
    setSelectedActions(prev => prev.map(a => a.adjustmentType === adjustmentType ? { ...a, adjustmentValue } : a));
  };

  const getActionValue = (adjustmentType: AvailabilityAction['adjustmentType']): string | number => {
    return selectedActions.find(a => a.adjustmentType === adjustmentType)?.adjustmentValue || '';
  };

  const isActionSelected = (adjustmentType: AvailabilityAction['adjustmentType']): boolean => {
    return selectedActions.some(a => a.adjustmentType === adjustmentType);
  };

  // Calculate total available rooms from selected room types
  const getTotalAvailableRooms = () => {
    if (selectedRoomTypes.length === 0) {
      return roomTypesWithVariants.reduce((sum, rt) => sum + rt.roomCount, 0);
    }
    return roomTypesWithVariants
      .filter(rt => selectedRoomTypes.includes(rt.id))
      .reduce((sum, rt) => sum + rt.roomCount, 0);
  };

  const totalAvailableRooms = getTotalAvailableRooms();

  // Validation errors state
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    
    // Rule name validation
    if (!name.trim()) {
      errors.name = 'Rule name is required';
    } else if (name.trim().length < 3) {
      errors.name = 'Rule name must be at least 3 characters';
    } else if (name.trim().length > 100) {
      errors.name = 'Rule name must be less than 100 characters';
    }
    
    // Priority validation
    if (priority < 1) {
      errors.priority = 'Priority must be at least 1';
    } else if (priority > 999) {
      errors.priority = 'Priority must be less than 1000';
    }
    
    // Actions validation
    if (selectedActions.length === 0) {
      errors.actions = 'At least one action is required';
    } else {
      selectedActions.forEach((action, index) => {
        if (action.adjustmentType === 'setAvailability') {
          if (Number(action.adjustmentValue) < 0) {
            errors[`action_${index}`] = 'Availability cannot be negative';
          } else if (Number(action.adjustmentValue) > totalAvailableRooms) {
            errors[`action_${index}`] = `Availability cannot exceed available rooms (${totalAvailableRooms})`;
          }
        } else if (action.adjustmentType === 'setMinStay' || action.adjustmentType === 'setMaxStay' || action.adjustmentType === 'setExactStay') {
          if (Number(action.adjustmentValue) < 1) {
            errors[`action_${index}`] = 'Number of nights must be at least 1';
          } else if (Number(action.adjustmentValue) > 365) {
            errors[`action_${index}`] = 'Number of nights cannot exceed 365';
          }
        }
      });
    }
    
    // Date validation
    if (!startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (endDate && startDate && endDate < startDate) {
      errors.endDate = 'End date must be after start date';
    }
    
    // Execution schedule validation
    if (processingFrequency === 'weekly' && executionDays.length === 0) {
      errors.executionDays = 'Select at least one day for weekly execution';
    }
    
    if (processingFrequency === 'monthly' && executionDays.length === 0) {
      errors.executionDays = 'Select at least one day for monthly execution';
    }
    
    if (['hourly', 'daily', 'weekly', 'monthly'].includes(processingFrequency)) {
      if (!executionStartDate) {
        errors.executionStartDate = 'Execution Start Date is required';
      }
      if (executionEndDate && executionStartDate && executionEndDate < executionStartDate) {
        errors.executionEndDate = 'Execution End Date must be after Execution Start Date';
      }
    }
    
    return errors;
  }, [name, priority, selectedActions, totalAvailableRooms, startDate, endDate, processingFrequency, executionDays, executionStartDate, executionEndDate]);

  const isFormValid = Object.keys(validationErrors).length === 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== AVAILABILITY RULE FORM SUBMISSION ===');
    console.log('selectedOTAChannels state:', selectedOTAChannels);
    console.log('selectedOTAChannels length:', selectedOTAChannels.length);
    
    // Prevent submission if validation fails
    if (!isFormValid) {
      return;
    }

    // Build the dialog format rule
    const dialogRule: Omit<AvailabilityRule, 'id' | 'createdAt' | 'updatedAt'> & { applicateCmNames?: string[] } = {
      name,
      priority,
      isActive: editingRule?.isActive ?? true,
      description: description || undefined,
      roomTypes: selectedRoomTypes,
      selectedVariants: selectedVariants.length > 0 ? selectedVariants : undefined,
      otaChannels: selectedOTAChannels,
      applicateCmNames: applicateCmNames,
      action: selectedActions, // Changed from 'actions' to 'action'
      startDate: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
      endDate: endDate ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}` : undefined,
      daysOfWeek: selectedDays.length > 0 ? selectedDays : undefined,
      timeOfDay: timeOfDay !== 'all' ? timeOfDay : undefined,
      processingFrequency,
      executionDays: executionDays.length > 0 ? executionDays : undefined,
      executionTime: processingFrequency !== 'manual' ? executionTime : undefined,
      executionStartDate: executionStartDate ? new Date(Date.UTC(executionStartDate.getFullYear(), executionStartDate.getMonth(), executionStartDate.getDate())).toISOString() : undefined,
      executionEndDate: executionEndDate ? new Date(Date.UTC(executionEndDate.getFullYear(), executionEndDate.getMonth(), executionEndDate.getDate())).toISOString() : undefined,
    };

    console.log('Selected OTA Channels:', selectedOTAChannels);
    console.log('Dialog Rule otaChannels:', dialogRule.otaChannels);

    // Transform to API format (branchId is used as property_id in API)
    const apiRule = transformAvailabilityRuleForAPI(
      dialogRule,
      orgId || '0',
      propertyId || '0',
      branchId || 0,
      api_key || ''
    );

    console.log('API Rule otaChannels:', apiRule.otaChannels);

    // Add id to the API rule if editing
    if (editingRule?.id) {
      apiRule.id = editingRule.id;
    }

    onSave(apiRule);
    // Don't close here - let parent handle closing on success
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingRule && editingRule.id ? 'Edit Availability Rule' : 'Create New Availability Rule'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Define availability controls for specific date ranges and conditions
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <DialogBody className="space-y-6 max-h-[400px] overflow-y-auto">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ruleName">Rule Name <span className="text-destructive">*</span></Label>
              <Input
                id="ruleName"
                placeholder="e.g., Weekend Minimum Stay"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={validationErrors.name ? 'border-destructive' : ''}
              />
              {validationErrors.name && (
                <p className="text-xs text-destructive">{validationErrors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority <span className="text-destructive">*</span></Label>
              <Input
                id="priority"
                type="number"
                min={1}
                max={999}
                value={priority}
                onChange={(e) => {
                  const value = e.target.value === '' ? 1 : Number(e.target.value);
                  setPriority(value);
                }}
                className={validationErrors.priority ? 'border-destructive' : ''}
              />
              {validationErrors.priority && (
                <p className="text-xs text-destructive">{validationErrors.priority}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-muted-foreground">(Optional)</span>
            </Label>
            <Input
              id="description"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <Separator />

          {/* Target Scope */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold">Target Scope</h3>

            {/* Room Types */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Room Types</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={selectAllRoomTypes}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedRoomTypes([]);
                      setSelectedVariants([]);
                    }}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Select specific room types or leave empty for all
              </p>

              {/* Selected chips */}
              {selectedRoomTypes.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 p-3">
                  {selectedRoomTypes.map((id) => {
                    const room = roomTypesWithVariants.find((r) => r.id === id);
                    return (
                      <Badge key={id} variant="secondary" className="gap-1 pl-2">
                        {room?.name}
                        <button
                          type="button"
                          onClick={() => toggleRoomType(id)}
                          className="ml-1 rounded-full hover:bg-background/50"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}

              {isLoadingRooms ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="rounded-lg border border-border p-4 space-y-3 max-h-64 overflow-y-auto">
                  {roomTypesWithVariants.map((roomType) => (
                    <div key={roomType.id} className="space-y-2">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id={`rt-${roomType.id}`}
                          checked={selectedRoomTypes.includes(roomType.id)}
                          onCheckedChange={() => toggleRoomType(roomType.id)}
                          className="mt-0.5"
                        />
                        <label htmlFor={`rt-${roomType.id}`} className="flex-1 cursor-pointer">
                          <span className="font-medium">{roomType.name}</span>
                          <p className="text-xs text-muted-foreground">
                            {roomType.adults}A / {roomType.children}C · ₹{roomType.basePrice}/night · {roomType.roomCount} rooms
                          </p>
                        </label>
                      </div>
                      {/* Show variants when room type is selected */}
                      {selectedRoomTypes.includes(roomType.id) && roomType.variants.length > 0 && (
                        <div className="ml-8 pl-4 border-l-2 border-accent/30 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Select Variants ({getVariantsForRoomType(roomType.id).length}/{roomType.variants.length})
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs px-2"
                                onClick={() => selectAllVariantsForRoomType(roomType.id)}
                              >
                                {getVariantsForRoomType(roomType.id).length === roomType.variants.length ? 'Deselect All' : 'Select All'}
                              </Button>
                              {getVariantsForRoomType(roomType.id).length === 0 && (
                                <span className="text-xs text-muted-foreground italic">All included if none selected</span>
                              )}
                            </div>
                          </div>
                          {roomType.variants.map((variant) => {
                            const variantKey = `avail-${roomType.id}-${variant.id}`;
                            const isSelected = isVariantSelected(roomType.id, variant.id);
                            return (
                              <label
                                key={variantKey}
                                htmlFor={variantKey}
                                className={cn(
                                  "flex items-center gap-2 py-1.5 px-3 rounded-md cursor-pointer transition-colors",
                                  isSelected 
                                    ? "bg-accent/20 border border-accent/40" 
                                    : "bg-muted/50 hover:bg-muted"
                                )}
                              >
                                <Checkbox
                                  id={variantKey}
                                  checked={isSelected}
                                  onCheckedChange={() => toggleVariant(roomType.id, variant.id, variant.code, variant.name)}
                                />
                                <Badge variant="outline" className="text-xs font-mono">
                                  {variant.code}
                                </Badge>
                                <span className="text-sm font-medium">{variant.name}</span>
                                <span className="text-xs text-muted-foreground ml-auto">
                                  ₹{variant.basePrice} · {variant.maxAdults}A/{variant.maxChildren}C
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* OTA Channels - Hide when api_key is available */}
            {!api_key && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>OTA Channels</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedOTAChannels(otaChannels.map((c) => c.code))}
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedOTAChannels([])}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Select specific channels or leave empty for all
              </p>

              {/* Selected chips */}
              {selectedOTAChannels.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 p-3">
                  {selectedOTAChannels.map((id) => {
                    const channel = otaChannels.find((c) => c.code === id);
                    return (
                      <Badge key={id} variant="secondary" className="gap-1 pl-2">
                        {channel?.name}
                        <button
                          type="button"
                          onClick={() => toggleOTAChannel(channel?.code)}
                          className="ml-1 rounded-full hover:bg-background/50"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}

              <div className="rounded-lg border border-border p-4 space-y-2 max-h-40 overflow-y-auto">
                {otaChannels.map((channel) => (
                  <div key={channel.id} className="flex items-center gap-3">
                    <Checkbox
                      id={`ota-${channel.id}`}
                      checked={selectedOTAChannels.includes(channel.code)}
                      onCheckedChange={() => toggleOTAChannel(channel.code)}
                    />
                    <label htmlFor={`ota-${channel.id}`} className="cursor-pointer">
                      {channel.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Channel Manager Integration */}
            <div className="space-y-2">
              <Label>Channel Manager Integration</Label>
              <p className="text-sm text-muted-foreground">
                Select channel managers to sync this rule with.
              </p>

              <div className="rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="channex-cm-avail"
                    checked={applicateCmNames.includes('Channex')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setApplicateCmNames([...applicateCmNames, 'Channex']);
                      } else {
                        setApplicateCmNames(applicateCmNames.filter(name => name !== 'Channex'));
                      }
                    }}
                    className="mt-0.5"
                  />
                  <label htmlFor="channex-cm-avail" className="flex-1 cursor-pointer">
                    <span className="font-medium">Channex</span>
                    <p className="text-sm text-muted-foreground">
                      Sync this rule with Channex channel manager
                    </p>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Action */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold">Action</h3>
            <div className="space-y-4">
              <div className="space-y-3">
                <Label className={validationErrors.actions ? 'text-destructive' : ''}>
                  Action Type <span className="text-destructive">*</span>
                </Label>
                <p className="text-sm text-muted-foreground">
                  Select one or more actions to apply
                </p>
                {validationErrors.actions && (
                  <p className="text-xs text-destructive">{validationErrors.actions}</p>
                )}

                {/* Action Type Checkboxes */}
                <div className="space-y-3 rounded-lg border border-border p-4">
                  {actionTypes.map((actionType) => (
                    <div key={actionType.value} className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id={`action-${actionType.value}`}
                          checked={isActionSelected(actionType.value as AvailabilityAction['adjustmentType'])}
                          onCheckedChange={() => toggleActionType(actionType.value as AvailabilityAction['adjustmentType'])}
                          className="mt-0.5"
                        />
                        <label htmlFor={`action-${actionType.value}`} className="flex-1 cursor-pointer">
                          <span className="font-medium">{actionType.label}</span>
                        </label>
                      </div>

                      {/* Show value input when action is selected */}
                      {isActionSelected(actionType.value as AvailabilityAction['adjustmentType']) && (
                        <div className="ml-8 space-y-2">
                          {actionType.value === 'setStatus' ? (
                            <Select
                              value={getActionValue('setStatus') as string}
                              onValueChange={(v) => updateActionValue('setStatus', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusValues.map((status) => (
                                  <SelectItem key={status.value} value={status.value}>
                                    {status.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : actionType.value === 'setMinStay' || actionType.value === 'setMaxStay' || actionType.value === 'setExactStay' ? (
                            <div className="space-y-2">
                              <Label>Number of nights</Label>
                              <Input
                                type="number"
                                min={1}
                                max={365}
                                value={getActionValue(actionType.value as AvailabilityAction['adjustmentType']) as number}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? 1 : Number(e.target.value);
                                  updateActionValue(actionType.value as AvailabilityAction['adjustmentType'], value);
                                }}
                              />
                            </div>
                          ) : actionType.value === 'setAvailability' ? (
                            <div className="space-y-2">
                              <Label>Number of rooms</Label>
                              <Input
                                type="number"
                                min={0}
                                max={totalAvailableRooms}
                                value={getActionValue('setAvailability') as number}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? 0 : Number(e.target.value);
                                  updateActionValue('setAvailability', value);
                                }}
                              />
                              <p className="text-xs text-muted-foreground">
                                Available: {totalAvailableRooms} rooms
                              </p>
                            </div>
                          ) : actionType.value === 'adjustAvailability' ? (
                            <div className="space-y-2">
                              <Label>Adjustment (positive or negative)</Label>
                              <Input
                                type="number"
                                value={getActionValue('adjustAvailability') as number}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? 0 : Number(e.target.value);
                                  updateActionValue('adjustAvailability', value);
                                }}
                                placeholder="e.g., +5 or -3"
                              />
                              <p className="text-xs text-muted-foreground">
                                Use positive numbers to increase, negative to decrease
                              </p>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Applicability Period */}
          <div className="rounded-lg border border-border p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Calendar className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">Applicability Period</p>
                <p className="text-sm text-muted-foreground">
                  Define when this rule is active and valid
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>
                  End Date <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        validationErrors.endDate && "border-destructive"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'No end date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {validationErrors.endDate && (
                  <p className="text-xs text-destructive">{validationErrors.endDate}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Days of Week <span className="text-muted-foreground">(Optional)</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Select days or leave empty to apply every day
              </p>
              <div className="flex gap-2">
                {daysOfWeek.map((day, index) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(index)}
                    className={cn('day-selector', selectedDays.includes(index) && 'active')}
                  >
                    {day}
                  </button>
                ))}
              </div>
              {selectedDays.length > 0 && (
                <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2 mt-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Active on: {getSelectedDayNames()}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Time of Day</Label>
              <Select value={timeOfDay} onValueChange={setTimeOfDay}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Day (24 hours)</SelectItem>
                  <SelectItem value="morning">Morning (6AM - 12PM)</SelectItem>
                  <SelectItem value="afternoon">Afternoon (12PM - 6PM)</SelectItem>
                  <SelectItem value="evening">Evening (6PM - 12AM)</SelectItem>
                  <SelectItem value="night">Night (12AM - 6AM)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Execution Schedule */}
          <div className="rounded-lg border border-border p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Settings className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">Execution Schedule</p>
                <p className="text-sm text-muted-foreground">
                  Define when the system processes and applies this rule
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Processing Frequency</Label>
              <Select
                value={processingFrequency}
                onValueChange={(v) =>
                  setProcessingFrequency(v as typeof processingFrequency)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {frequencies.map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {processingFrequency === 'manual' && (
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm font-medium">Manual Execution</p>
                <p className="text-sm text-muted-foreground">
                  This rule will only run when manually triggered via the "Run Now" button.
                </p>
              </div>
            )}

            {processingFrequency === 'immediate' && (
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm font-medium">Immediate Execution</p>
                <p className="text-sm text-muted-foreground">
                  This rule will run once immediately after saving.
                </p>
              </div>
            )}

            {processingFrequency === 'hourly' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-sm font-medium">Hourly Execution</p>
                  <p className="text-sm text-muted-foreground">
                    This rule will run automatically every hour.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!validationErrors.executionStartDate}>
                    <Label className={validationErrors.executionStartDate ? 'text-destructive' : ''}>
                      Execution Start Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionStartDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionStartDate ? format(executionStartDate, 'PPP') : 'Pick a start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionStartDate}
                          onSelect={setExecutionStartDate}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionStartDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionStartDate}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Execution End Date <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <CalendarComponent
                            mode="single"
                            selected={executionEndDate}
                            onSelect={setExecutionEndDate}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {executionEndDate && (
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setExecutionEndDate(undefined)}
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {processingFrequency === 'daily' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted p-3">
                  <p className="text-sm font-medium">Daily Execution</p>
                  <p className="text-sm text-muted-foreground">
                    This rule will run once per day at the specified time.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Execution time:</Label>
                  <Input
                    type="time"
                    value={executionTime}
                    onChange={(e) => setExecutionTime(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!validationErrors.executionStartDate}>
                    <Label className={validationErrors.executionStartDate ? 'text-destructive' : ''}>
                      Execution Start Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            validationErrors.executionStartDate && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionStartDate ? format(executionStartDate, 'PPP') : 'Pick a start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionStartDate}
                          onSelect={setExecutionStartDate}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {validationErrors.executionStartDate && (
                      <p className="text-sm text-destructive">{validationErrors.executionStartDate}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Execution End Date <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <CalendarComponent
                            mode="single"
                            selected={executionEndDate}
                            onSelect={setExecutionEndDate}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {executionEndDate && (
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setExecutionEndDate(undefined)}
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {processingFrequency === 'weekly' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">Weekly Execution</span>
                  </div>
                  <Label className="text-sm">Days of week:</Label>
                  <div className="flex gap-2 mt-2">
                    {daysOfWeek.map((day, index) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleExecutionDay(index)}
                        className={cn(
                          'day-selector',
                          executionDays.includes(index) && 'active'
                        )}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  {validationErrors.executionDays && (
                    <p className="text-xs text-destructive mt-2">{validationErrors.executionDays}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Execution time:</Label>
                  <Input
                    type="time"
                    value={executionTime}
                    onChange={(e) => setExecutionTime(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    Execution End Date <span className="text-muted-foreground">(Optional)</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={executionEndDate}
                        onSelect={setExecutionEndDate}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}

            {processingFrequency === 'monthly' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">Monthly Execution</span>
                  </div>
                  <Label className="text-sm">Days of month:</Label>
                  <div className="grid grid-cols-7 gap-1 mt-2">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleExecutionDay(day)}
                        className={cn(
                          'h-9 rounded-md text-sm font-medium transition-colors border',
                          executionDays.includes(day)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border hover:bg-muted'
                        )}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  {validationErrors.executionDays && (
                    <p className="text-xs text-destructive mt-2">{validationErrors.executionDays}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Execution time:</Label>
                  <Input
                    type="time"
                    value={executionTime}
                    onChange={(e) => setExecutionTime(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    Execution End Date <span className="text-muted-foreground">(Optional)</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {executionEndDate ? format(executionEndDate, 'PPP') : 'No end date (runs indefinitely)'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={executionEndDate}
                        onSelect={setExecutionEndDate}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}

            {processingFrequency === 'specific' && (
              <div className="space-y-3">
                <div className="rounded-lg bg-muted/50 p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">Specific Date & Time</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Select a specific date and time to run this rule.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Execution Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {executionDays.length > 0 
                            ? format(new Date(executionDays[0]), 'PPP') 
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={executionDays.length > 0 ? new Date(executionDays[0]) : undefined}
                          onSelect={(date) => date && setExecutionDays([date.getTime()])}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Execution time:</Label>
                    <Input
                      type="time"
                      value={executionTime}
                      onChange={(e) => setExecutionTime(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-status-active hover:bg-status-active/90"
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Settings className="mr-2 h-4 w-4" />
                  Save Rule
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
